import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useToast } from "@/components/ui/use-toast";
import { useOrganizationSettings } from "@/hooks/organization-settings";
import { useUserName, useUserProfile } from "@/hooks/useUserName";
import { OrganizationSettingsSidebar } from "@/components/organization/OrganizationSettingsSidebar";
import { ProfileSection } from "@/components/organization/ProfileSection";
import { SubscriptionSection } from "@/components/organization/SubscriptionSection";
import { AppearanceSection } from "@/components/organization/AppearanceSection";
import { CurrencyMarketSection } from "@/components/organization/CurrencyMarketSection";
import { OrganizationSettingsLoading } from "@/components/organization/OrganizationSettingsLoading";

const OrganizationSettings = () => {
  const { 
    organization, 
    orgName, 
    setOrgName, 
    isLoading, 
    isSaving, 
    updateOrganization 
  } = useOrganizationSettings();
  
  const userName = useUserName();
  const userProfile = useUserProfile();
  const userEmail = organization?.owner_email || userProfile.email || "example@email.com";
  const userPhone = userProfile.phone || "+213770166734";
  
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  
  const initialActiveMenu = location.state?.activeMenu || "profile";
  
  const [activeMenu, setActiveMenu] = useState<string>(initialActiveMenu);

  useEffect(() => {
    if (location.state?.activeMenu) {
      setActiveMenu(location.state.activeMenu);
    }
  }, [location.state]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!orgName.trim()) {
      toast({
        title: "Erreur",
        description: "Le nom de l'organisation ne peut pas être vide.",
        variant: "destructive",
      });
      return;
    }
    
    const result = await updateOrganization(orgName);
    
    if (result.success) {
      toast({
        title: "Succès",
        description: "Les informations de l'organisation ont été mises à jour.",
      });
    } else {
      toast({
        title: "Erreur",
        description: result.error || "Une erreur s'est produite lors de la mise à jour.",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return <OrganizationSettingsLoading />;
  }

  const renderActiveSection = () => {
    switch (activeMenu) {
      case "profile":
        return (
          <ProfileSection 
            userName={userName}
            userEmail={userEmail}
            userPhone={userPhone}
          />
        );
      case "subscription":
      case "subscription-details":
      case "billing":
        return <SubscriptionSection organization={organization} />;
      case "appearance":
        return <AppearanceSection />;
      case "currency":
        return <CurrencyMarketSection />;
      default:
        return (
          <ProfileSection 
            userName={userName}
            userEmail={userEmail}
            userPhone={userPhone}
          />
        );
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 animate-fade-in dark:text-gray-100 transition-colors settings-main bg-pastel-blue-light dark:bg-[#121624]">
      <h1 className="text-3xl font-bold mb-2 text-gray-800 dark:text-gray-100">Paramètres Du Compte</h1>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-8">
        Découvrez ce qui se passe dans toutes vos landing pages
      </p>
      
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        <OrganizationSettingsSidebar 
          userName={userName}
          userEmail={userEmail}
          activeMenu={activeMenu}
          setActiveMenu={setActiveMenu}
        />
        
        {renderActiveSection()}
      </div>
    </div>
  );
};

export default OrganizationSettings;
